import React, { useState, useEffect } from 'react';
import { bikeCommentsAPI, adminAPI } from '../services/api';
import Icon from './Icon';

// Comment categories with icons
const getCategories = (darkMode) => ({
  general: { iconName: 'chat', label: 'General', darkMode },
  maintenance: { iconName: 'service', label: 'Maintenance', darkMode },
  issue: { iconName: 'warning', label: 'Issue', darkMode },
  modification: { iconName: 'edit', label: 'Modification', darkMode },
  transfer: { iconName: 'swap', label: 'Transfer', darkMode },
  inspection: { iconName: 'view', label: 'Inspection', darkMode }
});

/**
 * BikeComments - Manages bike comments with full edit history
 * @param {Object} props
 * @param {number} props.bikeId - The bike ID
 * @param {boolean} props.isAdmin - Whether user is admin
 * @param {Object} props.currentUser - Current user object
 * @param {boolean} props.darkMode - Whether dark mode is enabled
 */
function BikeComments({ bikeId, isAdmin = false, currentUser, darkMode = true }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // New comment form
  const [newComment, setNewComment] = useState('');
  const [newCategory, setNewCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  
  // Edit mode
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState('general');
  
  // History modal
  const [historyModal, setHistoryModal] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [bikeId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = isAdmin 
        ? await adminAPI.getBikeComments(bikeId)
        : await bikeCommentsAPI.getComments(bikeId);
      setComments(response.data.comments || []);
    } catch (err) {
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const response = isAdmin
        ? await adminAPI.addBikeComment(bikeId, { comment_text: newComment, category: newCategory })
        : await bikeCommentsAPI.addComment(bikeId, { comment_text: newComment, category: newCategory });
      
      setComments([response.data.comment, ...comments]);
      setNewComment('');
      setNewCategory('general');
      setSuccess('Comment added!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editText.trim()) return;

    try {
      const response = await bikeCommentsAPI.updateComment(bikeId, commentId, {
        comment_text: editText,
        category: editCategory
      });
      
      setComments(comments.map(c => c.id === commentId ? response.data.comment : c));
      setEditingId(null);
      setEditText('');
      setSuccess('Comment updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await bikeCommentsAPI.deleteComment(bikeId, commentId);
      setComments(comments.filter(c => c.id !== commentId));
      setSuccess('Comment deleted!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete comment');
    }
  };

  const handleTogglePin = async (commentId) => {
    try {
      const response = await bikeCommentsAPI.togglePinComment(bikeId, commentId);
      setComments(comments.map(c => 
        c.id === commentId ? { ...c, is_pinned: response.data.is_pinned } : c
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to pin/unpin comment');
    }
  };

  const handleViewHistory = async (comment) => {
    setHistoryModal(comment);
    setLoadingHistory(true);
    
    try {
      const response = await bikeCommentsAPI.getCommentHistory(bikeId, comment.id);
      setHistory(response.data.history || []);
    } catch (err) {
      setError('Failed to load edit history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{
        background: 'var(--bg-overlay-light)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div className="loading">Loading comments...</div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-overlay-light)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px'
    }}>
      <h2 style={{ margin: '0 0 20px 0', color: '#ffffff', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icon name="chat" color="secondary" darkMode={darkMode} size={18} />
        Comments {comments.length > 0 && <span style={{ opacity: 0.5 }}>({comments.length})</span>}
      </h2>

      {error && (
        <div style={{
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#f87171',
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px',
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#4A8C18',
          fontSize: '13px'
        }}>
          {success}
        </div>
      )}

      {/* Add new comment form */}
      <form onSubmit={handleAddComment} style={{ marginBottom: '24px' }}>
        <div style={{ marginBottom: '12px' }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment about this bike..."
            rows={3}
            maxLength={5000}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '12px',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
              background: darkMode ? 'var(--bg-overlay-medium)' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1a1a1a',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
              background: darkMode ? 'var(--bg-overlay-medium)' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1a1a1a',
              fontSize: '14px'
            }}
          >
            {Object.entries(getCategories(darkMode)).map(([key, { iconName, label }]) => (
              <option key={key} value={key} style={{ background: darkMode ? '#1a1a3e' : '#ffffff' }}>
                {label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="btn btn-primary"
            style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {submitting ? (
              <>
                <Icon name="loading" color="primary" darkMode={darkMode} size={16} />
                Sending...
              </>
            ) : (
              <>
                <Icon name="edit" color="primary" darkMode={darkMode} size={16} />
                Add comment
              </>
            )}
          </button>
        </div>
      </form>

      {/* Comments list */}
      {comments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'rgba(255,255,255,0.4)'
        }}>
          <div style={{ marginBottom: '12px', opacity: 0.3 }}>
            <Icon name="chat" color="muted" darkMode={darkMode} size={48} />
          </div>
          <p style={{ margin: 0, fontSize: '14px' }}>
            No comments yet. Be the first!
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {comments.map((comment) => {
            const categories = getCategories(darkMode);
            const category = categories[comment.category] || categories.general;
            const canEdit = comment.can_edit;
            const canDelete = comment.can_delete;
            const hasHistory = comment.edit_history && comment.edit_history.length > 0;
            const isEditing = editingId === comment.id;

            return (
              <div
                key={comment.id}
                style={{
                  padding: '16px',
                  background: comment.is_pinned 
                    ? 'rgba(61, 90, 32, 0.05)' 
                    : 'rgba(255,255,255,0.02)',
                  border: comment.is_pinned 
                    ? '1px solid rgba(61, 90, 32, 0.2)' 
                    : '1px solid rgba(255,255,255,0.05)',
                  borderRadius: '12px'
                }}
              >
                {/* Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '4px 10px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      <Icon name={category.iconName} color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      {category.label}
                    </span>
                    {comment.is_pinned && (
                      <span style={{
                        padding: '4px 8px',
                        background: 'rgba(61, 90, 32, 0.2)',
                        color: '#3d5a20',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Icon name="view" color="brand" darkMode={darkMode} size={12} />
                        Pinned
                      </span>
                    )}
                    {hasHistory && (
                      <button
                        type="button"
                        onClick={() => handleViewHistory(comment)}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(245, 158, 11, 0.15)',
                          color: '#fbbf24',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Icon name="history" color="warning" darkMode={darkMode} size={12} />
                        Edited
                      </button>
                    )}
                  </div>
                  
                  {/* Actions */}
                  {(canEdit || canDelete || isAdmin) && !isEditing && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleTogglePin(comment.id)}
                          style={{
                            padding: '4px 8px',
                            background: 'var(--bg-overlay-medium)',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: 'rgba(255,255,255,0.6)'
                          }}
                          title={comment.is_pinned ? 'Unpin' : 'Pin'}
                        >
                          <Icon
                            name={comment.is_pinned ? 'view' : 'flag'}
                            color="secondary"
                            darkMode={darkMode}
                            size={12}
                          />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(comment.id);
                            setEditText(comment.comment_text);
                            setEditCategory(comment.category);
                          }}
                          style={{
                            padding: '4px 8px',
                            background: 'var(--bg-overlay-medium)',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: 'rgba(255,255,255,0.6)'
                          }}
                        >
                          <Icon name="edit" color="secondary" darkMode={darkMode} size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{
                            padding: '4px 8px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: '#f87171'
                          }}
                        >
                          <Icon name="delete" color="error" darkMode={darkMode} size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                {isEditing ? (
                  <div>
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      maxLength={5000}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'var(--bg-overlay-medium)',
                        color: '#ffffff',
                        fontSize: '14px',
                        marginBottom: '12px',
                        resize: 'vertical'
                      }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid rgba(255,255,255,0.1)',
                          background: 'var(--bg-overlay-medium)',
                          color: '#ffffff',
                          fontSize: '13px'
                        }}
                      >
                        {Object.entries(getCategories(darkMode)).map(([key, { iconName, label }]) => (
                          <option key={key} value={key} style={{ background: '#1a1a3e' }}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleUpdateComment(comment.id)}
                        className="btn btn-sm btn-primary"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(null);
                          setEditText('');
                        }}
                        className="btn btn-sm btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{ 
                    margin: 0, 
                    color: 'rgba(255,255,255,0.85)', 
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                    fontSize: '14px'
                  }}>
                    {comment.comment_text}
                  </p>
                )}

                {/* Footer */}
                <div style={{ 
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      color: 'rgba(255,255,255,0.6)', 
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {comment.author_name || comment.author_username}
                    </span>
                    {comment.company_name && (
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(102, 126, 234, 0.15)',
                        color: 'rgba(102, 126, 234, 0.9)',
                        borderRadius: '6px',
                        fontSize: '10px'
                      }}>
                        {comment.company_name}
                      </span>
                    )}
                    {comment.author_role === 'company_admin' && (
                      <span style={{
                        padding: '2px 8px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#4A8C18',
                        borderRadius: '6px',
                        fontSize: '10px'
                      }}>
                        Admin
                      </span>
                    )}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                    {formatDate(comment.created_at)}
                    {comment.updated_at !== comment.created_at && ' (edited)'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History Modal */}
      {historyModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '24px'
          }}
          onClick={() => setHistoryModal(null)}
        >
          <div
            style={{
              background: '#1a1a3e',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="history" color="secondary" darkMode={darkMode} size={18} />
                Edit History
              </h2>
              <button
                type="button"
                onClick={() => setHistoryModal(null)}
                style={{
                  background: 'var(--bg-overlay-medium)',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  color: 'rgba(255,255,255,0.6)'
                }}
              >
                x
              </button>
            </div>

            {/* Current version */}
            <div style={{
              padding: '16px',
              background: 'rgba(16, 185, 129, 0.05)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ 
                fontSize: '11px', 
                color: '#4A8C18', 
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                CURRENT VERSION
              </div>
              <p style={{ 
                margin: 0, 
                color: 'rgba(255,255,255,0.8)', 
                whiteSpace: 'pre-wrap',
                fontSize: '14px'
              }}>
                {historyModal.comment_text}
              </p>
            </div>

            {loadingHistory ? (
              <div className="loading">Loading history...</div>
            ) : history.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                No edit history.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {history.map((h, index) => (
                  <div
                    key={h.id || index}
                    style={{
                      padding: '14px',
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: '10px'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      fontSize: '12px'
                    }}>
                      <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {h.edited_by_name || h.edited_by_username}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {formatDate(h.edited_at)}
                      </span>
                    </div>
                    <div style={{
                      padding: '10px',
                      background: 'rgba(239, 68, 68, 0.05)',
                      borderRadius: '6px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ fontSize: '10px', color: '#f87171', marginBottom: '4px' }}>
                        BEFORE:
                      </div>
                      <p style={{ 
                        margin: 0, 
                        color: 'rgba(255,255,255,0.6)', 
                        fontSize: '13px',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {h.previous_text || '(empty)'}
                      </p>
                    </div>
                    <div style={{
                      padding: '10px',
                      background: 'rgba(16, 185, 129, 0.05)',
                      borderRadius: '6px'
                    }}>
                      <div style={{ fontSize: '10px', color: '#4A8C18', marginBottom: '4px' }}>
                        AFTER:
                      </div>
                      <p style={{ 
                        margin: 0, 
                        color: 'rgba(255,255,255,0.6)', 
                        fontSize: '13px',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {h.new_text || '(empty)'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BikeComments;

